import { Injectable } from '@angular/core';
import { map, Observable } from 'rxjs';
import { HttpClient } from '@angular/common/http';
import { TranslateService } from '@ngx-translate/core';
import { Dictionary } from '@app/core/models';
import { FieldInfo, StatsIntervalsOptions, StatsOptions } from './models';
import { PieResult, SeriesResult } from './models';
import { BYTES_TO_MB } from '@app/shared/consts';

@Injectable({
  providedIn: 'root'
})
export class StatsService {

  constructor(
    private http: HttpClient,
    private translate: TranslateService
  ) { }

  toQueryParams(statsOptions: StatsOptions | StatsIntervalsOptions, prefix?: string): Dictionary<string, string> {

    const input = statsOptions as StatsIntervalsOptions;
    const noPrefix: Dictionary<string, string> = { };

    if (input.aggFunction) {
      noPrefix.aggFunction = input.aggFunction;
    }
    if (input.value) {
      noPrefix.value = input.value;
    }
    if (input.group) {
      noPrefix.group = input.group;
    }
    if (input.filter) {
      noPrefix.filter = JSON.stringify(input.filter);
    }
    if (input.startDate !== undefined) {
      noPrefix.startDate = input.startDate ? input.startDate.toISOString() : `${input.startDate}`;
    }
    if (input.endDate !== undefined) {
      noPrefix.endDate = input.endDate ? input.endDate.toISOString() : `${input.endDate}`;
    }
    if (input.amount) {
      noPrefix.amount = `${input.amount}`;
    }
    if (input.unit) {
      noPrefix.unit = input.unit;
    }

    if (!prefix) {
      return noPrefix;
    }

    const prefixed = { };
    Object.keys(noPrefix).forEach(key => prefixed[`${prefix}__${key}`] = noPrefix[key]);
    return prefixed;
  }

  fromQueryParams(params: Dictionary<string, string>, prefix?: string): StatsOptions | StatsIntervalsOptions {
    let unprefixed: Dictionary<string, string> = { };

    if (!prefix) {
      unprefixed = params;
    } else {
      Object.keys(params).forEach(key => {
        if (key.startsWith(`${prefix}__`)) {
          const originalKey = key.replace(`${prefix}__`, '');
          unprefixed[originalKey] = params[key];
        }
      });
    }

    if (Object.keys(unprefixed).length === 0) {
      return null;
    }

    const options = { } as StatsIntervalsOptions;

    if (unprefixed.filter) {
      if (typeof unprefixed.filter === 'string') {
        options.filter = JSON.parse(unprefixed.filter);
      } else {
        options.filter = unprefixed.filter;
      }
    }
    if (unprefixed.startDate) {
      if (Date.parse(unprefixed.startDate)) {
        options.startDate = new Date(unprefixed.startDate);
      } else {
        options.startDate = null;
      }
    }
    if (unprefixed.endDate) {
      if (Date.parse(unprefixed.endDate)) {
        options.endDate = new Date(unprefixed.endDate);
      } else {
        options.endDate = null;
      }
    }
    if (unprefixed.amount) {
      options.amount = +unprefixed.amount;
    }
    if (unprefixed.aggFunction) {
      options.aggFunction = unprefixed.aggFunction;
    }
    if (unprefixed.value) {
      options.value = unprefixed.value;
    }
    if (unprefixed.group) {
      options.group = unprefixed.group;
    }
    if (unprefixed.unit) {
      options.unit = unprefixed.unit;
    }

    return options;
  }

  getSizeFormatterFn() {
    const suffix = this.translate.instant('anms.mb');

    return function() {
      const megabytesValue = (Math.floor(this.y / BYTES_TO_MB * 100) / 100);
      return `${megabytesValue.toLocaleString()} ${suffix}`;
    };
  }

  getFieldsValues(): Observable<Dictionary<string, FieldInfo>> {
    return this.http.get('/api/stats/fields').pipe(
      map((result: any) => FieldInfo.deserializeDict(result))
    );
  }

  getSeries(options: StatsIntervalsOptions): Observable<SeriesResult> {
    const params = this.toQueryParams(options);

    return this.http.get('/api/stats/series', { params }).pipe(
      map((result: any) => new SeriesResult(result))
    );
  }

  getPie(options: StatsOptions): Observable<PieResult> {
    const params = this.toQueryParams(options);

    return this.http.get('/api/stats/pie', { params }).pipe(
      map((result: any) => new PieResult(result))
    );
  }
}
